import { inject, Injectable } from '@angular/core';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ProductStoreSelector } from '@common/selectors/product.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { combineLatest, defer, map, Observable, of, shareReplay, interval, startWith } from 'rxjs';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { ShopsService } from '@core/services/shops/shops.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { cartHasProductTag, mergeCartProductTagCatalog } from './cart-product-tag';
import { ExpressionLanguageUtile } from './expression-language.utile';
import { DateUtils } from '@shared/utils/date/date.utils';
import { ComoService } from '@core/services/como/como.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { PongoService } from '@core/services/pongo/pongo.service';
import { injectConfirmationPageContext } from './context-expression-language.util';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { visibilityRecurrenceMatches } from './visibility-recurrence/visibility-recurrence.runtime';
import { MarketplaceOrderingAvailableService } from '@core/services/marketplace-ordering/marketplace-ordering-available.service';
import { JourneyParametersRuntimeService } from './journey-parameters-runtime.service';
import { withVisitorLocaleForVisibility } from './locale-visibility-context';
import { VisibilityTestModeService } from './visibility-test-mode.service';

@Injectable({ providedIn: 'root' })
export class EvaluateBlockVisibilityService {
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly productStoreSelector = inject(ProductStoreSelector);
  private readonly shopsService = inject(ShopsService);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly requestedCatalogShopIds = new Set<string>();
  private readonly comoService = inject(ComoService);
  private readonly zerosixService = inject(ZerosixService);
  private readonly pongoService = inject(PongoService);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly marketplaceOrderingAvailableService = inject(
    MarketplaceOrderingAvailableService,
  );
  private readonly journeyParametersRuntime = inject(JourneyParametersRuntimeService);
  private readonly visibilityTestMode = inject(VisibilityTestModeService);

  private readonly confirmationPageContext = injectConfirmationPageContext();

  // Create a time observable that starts immediately and updates every minute
  private readonly time$ = interval(60000).pipe(
    startWith(0), // Start immediately
    map(() => DateUtils.dayjsInMarketplaceTimezone().toDate()),
    shareReplay(1),
  );

  // Create a loyalty membership observable that checks if user is member of any loyalty program
  private readonly isLoyaltyMember$ = combineLatest({
    isComoMember: this.comoService.isUserComoAccountEnabledOnMarketplaceLevel$(),
    isZerosixMember: this.zerosixService.isUserZeroSixCardEnabledOnMarketplaceLevel$(),
    isPongoMember: this.pongoService.isUserPongoCustomerEnabledOnMarketplaceLevel$(),
  }).pipe(
    map(
      ({ isComoMember, isZerosixMember, isPongoMember }) =>
        isComoMember || isZerosixMember || isPongoMember,
    ),
    shareReplay(1),
  );

  private readonly cartProductCatalog$ = combineLatest({
    products: this.productStoreSelector.selectProducts$,
    shops: this.shopStoreSelector.selectShops,
  }).pipe(
    map(({ products, shops }) => mergeCartProductTagCatalog(products, shops)),
    shareReplay(1),
  );

  private readonly context$ = combineLatest({
    cart: this.cartStoreSelector.selectActive$,
    cartProductCatalog: this.cartProductCatalog$,
    user: this.authStoreSelector.selectUser$,
    order: this.orderStoreSelector.selectValidation$,
    // Use the shop bound to the displayed page (route lifecycle) so visibility
    // conditions don't leak the last selected shop across navigation.
    // Shop pages (shop, digital menu, kiosk shop) set `currentPage` from the route.
    // The cart page binds it from the active cart so per-shop blocks (suggestion
    // cards) still match. Other non-shop pages (cart funnel, payment, shared-order
    // validation, …) still leave `shop` null unless they bind it too.
    shop: this.shopStoreSelector.selectCurrentPage$,
    parameters: this.settingsStoreSelector.selectParameters$,
    journeyParameters: this.journeyParametersRuntime.params$,
    locale: this.settingsStoreSelector.selectAppLocale,
    device: this.settingsStoreSelector.selectDeviceScreen,
    time: this.time$,
    isLoyaltyMember: this.isLoyaltyMember$,
    marketplace: this.marketplaceOrderingAvailableService.marketplaceContext$,
    filterCatalogIds: this.shopStoreSelector.selectActiveFilterCatalogIds$,
    isTestMode: this.visibilityTestMode.isTestMode$,
    ...this.confirmationPageContext,
  }).pipe(shareReplay(1));

  evaluate$(
    expression: string,
    options?: { additionalContext?: Record<string, any> },
  ): Observable<boolean> {
    const needsMarketplaceOrdering = expression.includes('marketplace.ordering_available');

    return defer(() => {
      if (needsMarketplaceOrdering) {
        this.marketplaceOrderingAvailableService.register();
      }
      return this.context$.pipe(
        tap(context => {
          if (expression.includes('cartHasProductTag')) {
            this.ensureCartProductCatalog(context.cart);
          }
        }),
        map(context => {
          const { cartProductCatalog, ...visibilityContext } = context;
          const journeyParameters = visibilityContext.journeyParameters ?? {};
          const userTags = Array.isArray(visibilityContext.user?.tags)
            ? visibilityContext.user.tags
            : [];
          const result = ExpressionLanguageUtile.evaluate(
            expression,
            withVisitorLocaleForVisibility({
              ...visibilityContext,
              ...options?.additionalContext,
              visibilityRecurrenceMatches,
              getJourneyParam: (key: string) => journeyParameters[key],
              userHasTag: (label: string) =>
                typeof label === 'string' &&
                label.length > 0 &&
                userTags.some(
                  (tag: { label?: string }) =>
                    typeof tag?.label === 'string' && tag.label === label,
                ),
              cartHasProductTag: (matchBy: string, value: string) =>
                cartHasProductTag(
                  visibilityContext.cart?.products,
                  cartProductCatalog,
                  matchBy,
                  value,
                ),
            }),
          );
          if (result === null) {
            console.error('Expression did not evaluate to a boolean:', expression, result);
            // Always return true if the expression is invalid
            return true;
          }
          return result;
        }),
        finalize(() => {
          if (needsMarketplaceOrdering) {
            this.marketplaceOrderingAvailableService.unregister();
          }
        }),
      );
    });
  }

  /**
   * Tags live on the shop catalog, not on cart lines. Load each cart shop once
   * so the condition can resolve after a cold start.
   */
  private ensureCartProductCatalog(
    cart: { products?: Array<{ shop_id?: string }> } | null | undefined,
  ): void {
    if (this.cb2PreviewState.isActive() || !this.marketplaceSelector.selectMarketplaceId()) {
      return;
    }

    for (const line of cart?.products ?? []) {
      const shopId = line?.shop_id;
      if (typeof shopId !== 'string' || !shopId || this.requestedCatalogShopIds.has(shopId)) {
        continue;
      }
      this.requestedCatalogShopIds.add(shopId);
      this.shopsService
        .loadShopById$(shopId)
        .pipe(
          take(1),
          catchError(() => of(undefined)),
        )
        .subscribe();
    }
  }
}
